import { ConfigurationFeatureFlag, ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import {
  PaymentCheckType,
  rejectDoublePayment,
  rejectFailedOrCancelledPayment,
} from '../../DirectPayments/models/payment';
import { setApplicationError } from '../../../stores/app-state-store';
import {
  getDuplicatePaymentFailure,
  getDuplicatePaymentStart,
  getDuplicatePaymentSuccess,
  getPaymentDetailsStart,
  getPaymentDetailsSuccess,
  setPaymentDetailsFailure,
} from '../../../stores/payment-store';
import { AppThunk } from '../../../stores/redux-store';
import { getProviderPaymentCapabilities } from '../../../stores/selected-provider-store';
import {
  getPaymentTransferDetailsStart,
  getPaymentTransferDetailsSuccess,
  setPaymentTransferDetailsFailure,
} from '../../../stores/payment-transfer-store';
import { getTransferStatus } from './api/Transfer';
import {
  getPaymentDetails,
  getProviderPaymentConditions,
  paymentPreInitiationCheck,
  paymentTransfersDetails,
} from './api/Payments';

export const getTransferStatusForPaymentRequest = async (paymentRequestId: string) => {
  const response = await paymentPreInitiationCheck(paymentRequestId, {
    [PaymentCheckType.FAILED_OR_CANCELLED_BEFORE]: false,
    [PaymentCheckType.INITIATED_BEFORE]: false,
  });

  if (response.data && response.data.transferBasicInfo) {
    const { transferBasicInfo } = response.data;

    if (transferBasicInfo[0] && transferBasicInfo[0].transferId) {
      return getTransferStatus(transferBasicInfo[0].transferId);
    }
  }

  return null;
};

export const getPaymentPreInitiation = (paymentRequestId: string, config: ConfigurationModel) => {
  const rejectDouble = rejectDoublePayment(config);
  const rejectFailed = rejectFailedOrCancelledPayment(config);

  return rejectDouble || rejectFailed
    ? paymentPreInitiationCheck(paymentRequestId, {
        [PaymentCheckType.FAILED_OR_CANCELLED_BEFORE]: rejectFailed ? false : null,
        [PaymentCheckType.INITIATED_BEFORE]: rejectDouble ? false : null,
      })
    : null;
};

export const fetchPaymentDetails =
  (paymentRequestId: string): AppThunk =>
  async (dispatch) => {
    dispatch(getPaymentDetailsStart());

    const paymentDetailsResponse = await getPaymentDetails(paymentRequestId);

    if (paymentDetailsResponse.isOk()) {
      dispatch(getPaymentDetailsSuccess(paymentDetailsResponse.data));
    } else {
      dispatch(setPaymentDetailsFailure());

      if (!paymentDetailsResponse.isOk()) {
        dispatch(
          setApplicationError({
            errorStatus: ErrorStatus.INTERNAL_ERROR,
            errorReason: ErrorReason.REQUEST_FAILED_FETCH_PAYMENT_INFO,
          })
        );
      }
    }
  };

export const fetchPaymentTransferDetails =
  (transferId: string): AppThunk =>
  async (dispatch) => {
    dispatch(getPaymentTransferDetailsStart());
    const paymentTransferDetailsResponse = await paymentTransfersDetails(transferId);

    if (paymentTransferDetailsResponse.isOk()) {
      dispatch(getPaymentTransferDetailsSuccess(paymentTransferDetailsResponse.data));
    } else {
      dispatch(setPaymentTransferDetailsFailure());

      if (!paymentTransferDetailsResponse.isOk()) {
        dispatch(
          setApplicationError({
            errorStatus: ErrorStatus.INTERNAL_ERROR,
            errorReason: ErrorReason.REQUEST_FAILED_FETCH_PAYMENT_TRANSFER_INFO,
          })
        );
      }
    }
  };

export const fetchDuplicatePayment =
  (paymentRequestId: string, config: ConfigurationModel): AppThunk =>
  async (dispatch) => {
    const paymentCheck = await getPaymentPreInitiation(paymentRequestId, config);

    if (!paymentCheck) return;

    const { data, status } = paymentCheck;

    if (!data) return;
    const { transferBasicInfo, initiatedBefore } = data;
    dispatch(getDuplicatePaymentStart());

    if (
      transferBasicInfo &&
      transferBasicInfo.length > 0 &&
      config.featureFlags.includes(ConfigurationFeatureFlag.REJECT_MULTIPLE_TRANSFERS)
    ) {
      dispatch(
        getDuplicatePaymentFailure(
          initiatedBefore
            ? PaymentCheckType.INITIATED_BEFORE
            : PaymentCheckType.FAILED_OR_CANCELLED_BEFORE
        )
      );
    }

    if (status !== 409) {
      dispatch(getDuplicatePaymentSuccess());
    } else {
      dispatch(
        getDuplicatePaymentFailure(
          initiatedBefore
            ? PaymentCheckType.INITIATED_BEFORE
            : PaymentCheckType.FAILED_OR_CANCELLED_BEFORE
        )
      );
    }
  };

export const fetchProviderPaymentConditions =
  (providerName: string): AppThunk =>
  async (dispatch) => {
    const paymentProviderDetailsResponse = await getProviderPaymentConditions(providerName);

    if (paymentProviderDetailsResponse.isOk()) {
      dispatch(getProviderPaymentCapabilities(paymentProviderDetailsResponse.data));
    } else {
      dispatch(
        setApplicationError({
          errorStatus: ErrorStatus.INTERNAL_ERROR,
          errorReason: ErrorReason.REQUEST_FAILED_FETCH_PROVIDER_PAYMENT_CONDITIONS,
        })
      );
    }
  };
